/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/9/22, 7:45 AM
 *
 */

import React from 'react';
import clsx from 'clsx';

// Default Button props
interface ButtonProps
  extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
    React.AriaAttributes {}

// Custom Props
type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonVariant = 'fill' | 'outline';
type ButtonColor = 'primary' | 'warning' | 'secondary' | 'error';

interface ButtonProps {
  size?: ButtonSize;
  variant?: ButtonVariant;
  color?: ButtonColor;
  loading?: boolean;
}

const buttonConfig = {
  primary: {
    fill: 'bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white hover:text-gray-100',
    outline: 'border-2 border-primary-500 text-primary-500  bg-opacity-0 hover:bg-primary-25',
  },
  secondary: {
    fill: 'bg-gray-500 hover:bg-gray-600 active:bg-gray-700 text-white hover:text-gray-100',
    outline: 'border-2 border-gray-500 text-gray-500  bg-opacity-0 hover:bg-secondary-25',
  },
  error: {
    fill: 'bg-error-500 hover:bg-error-600 active:bg-error-700 text-white hover:text-gray-100',
    outline: 'border-2 border-error-500 text-error-500  bg-opacity-0 hover:bg-error-25',
  },
  warning: {
    fill: 'bg-warning-500 hover:bg-warning-600 active:bg-warning-700 text-white hover:text-gray-100',
    outline: 'border-2 border-warning-500 text-warning-500  bg-opacity-0 hover:bg-warning-25',
  },

  sm: 'px-6 py-4 text-lg rounded-md active:shadow-sm',
  md: 'px-10 py-5 text-xl rounded-md active:shadow-md',
  lg: 'px-14 py-5 text-xl rounded-md active:shadow-md',
};

const Button = ({
  children,
  size = 'md',
  color = 'primary',
  variant = 'fill',
  loading = false,
  ...props
}: ButtonProps): JSX.Element => {
  return (
    <button
      className={clsx(
        'flex cursor-pointer items-center justify-center gap-2 transition-colors transition-shadow duration-200 disabled:cursor-not-allowed disabled:bg-opacity-70',
        buttonConfig[size],
        {
          [buttonConfig[color].fill]: variant === 'fill',
          [buttonConfig[color].outline]: variant === 'outline',
        }
      )}
      {...props}
    >
      {loading ? <div className="loading" /> : null}
      {children}
    </button>
  );
};

export default Button;
